import React, { FocusEventHandler } from "react";
import {
  HiOutlineInboxStack,
  HiOutlineMagnifyingGlass,
  HiOutlinePaperClip,
} from "react-icons/hi2";
import { ISupportConversation, SupportConversationStatus } from "../../data-access";
import { Spinner } from "../../ui";
import { formatListTime, getDisplayName, getInitials } from "./format";

interface IConversationListProps {
  conversations: ISupportConversation[];
  selectedId: string | null;
  status: SupportConversationStatus;
  searchValue: string;
  total: number;
  isLoading: boolean;
  onSelect: (id: string) => void;
  onStatusChange: (status: SupportConversationStatus) => void;
  onSearchChange: (value: string) => void;
}

const STATUS_TABS: SupportConversationStatus[] = ["open", "closed"];

const baseClass = "support-list";

export const ConversationList = ({
  conversations,
  selectedId,
  status,
  searchValue,
  total,
  isLoading,
  onSelect,
  onStatusChange,
  onSearchChange,
}: IConversationListProps): JSX.Element => {
  const handleSearch: FocusEventHandler<HTMLInputElement> = (event) => {
    onSearchChange(event.target.value);
  };

  const renderPreview = (conversation: ISupportConversation): JSX.Element => {
    // The backend sends an empty preview when the last message was a
    // screenshot with no text, so the row shows a paperclip instead of a gap.
    if (!conversation.preview) {
      if (!conversation.last_message_at) {
        return <span className={`${baseClass}__preview-empty`}>No messages yet</span>;
      }

      return (
        <span className={`${baseClass}__preview-attachment`}>
          <HiOutlinePaperClip size={14} />
          Attachment
        </span>
      );
    }

    return <>{conversation.preview}</>;
  };

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__filters`}>
        <div className={`${baseClass}__tabs`}>
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`${baseClass}__tab${tab === status ? " active" : ""}`}
              onClick={() => onStatusChange(tab)}
            >
              {tab === "open" ? "Open" : "Closed"}
            </button>
          ))}
        </div>
        <div className={`${baseClass}__search`}>
          <HiOutlineMagnifyingGlass size={16} />
          <input
            type="text"
            value={searchValue}
            placeholder="Search by name or email"
            onChange={handleSearch}
          />
        </div>
        <div className={`${baseClass}__total`}>
          {total} {total === 1 ? "conversation" : "conversations"}
        </div>
      </div>

      <div className={`${baseClass}__items`}>
        {isLoading && <Spinner />}

        {!isLoading && conversations.length === 0 && (
          <div className={`${baseClass}__empty`}>
            <HiOutlineInboxStack size={28} />
            <span>No {status} conversations</span>
          </div>
        )}

        {!isLoading &&
          conversations.map((conversation) => {
            const { user } = conversation;
            const displayName = getDisplayName(user.full_name, user.email);

            return (
              <button
                key={conversation.id}
                type="button"
                className={`${baseClass}__item${
                  conversation.id === selectedId ? " active" : ""
                }`}
                onClick={() => onSelect(conversation.id)}
              >
                <div className={`${baseClass}__avatar`}>
                  {getInitials(user.full_name, user.email)}
                </div>
                <div className={`${baseClass}__body`}>
                  <div className={`${baseClass}__row`}>
                    <span className={`${baseClass}__name`}>{displayName}</span>
                    <span className={`${baseClass}__time`}>
                      {formatListTime(conversation.last_message_at)}
                    </span>
                  </div>
                  <div className={`${baseClass}__row`}>
                    <span className={`${baseClass}__preview`}>
                      {renderPreview(conversation)}
                    </span>
                    {conversation.staff_unread_count > 0 && (
                      <span className={`${baseClass}__unread`}>
                        {conversation.staff_unread_count}
                      </span>
                    )}
                  </div>
                  <div className={`${baseClass}__row`}>
                    <span className={`${baseClass}__email`}>{user.email}</span>
                    {user.plan && (
                      <span className={`${baseClass}__plan`}>{user.plan}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
};
